/**
 * JAISIU-4452 — composer attachment inline previews.
 *
 * The composer chip used to have exactly two shapes:
 *
 *   - an `<img>` thumbnail, but **only** when the gateway had already
 *     produced a preview derivative (`previewUrl`) — in practice images;
 *   - a flat type icon for everything else.
 *
 * That left the operator staring at a bare glyph for a video, an audio
 * note, a PDF or a spreadsheet, with no indication of what they had
 * actually attached.
 *
 * This module decides, per chip, which inline presentation to use. It is
 * deliberately **pure** (no DOM, no Lit) so the decision table can be
 * unit-tested directly and reused by the chip renderer without dragging
 * the overlay into the picture.
 *
 * The decision table:
 *
 *   | kind        | inline presentation                                |
 *   |-------------|---------------------------------------------------|
 *   | `image`     | `<img>` thumbnail (36×36, unchanged)                |
 *   | `video`     | `<video>` preview frame (muted, `playsinline`)      |
 *   | `audio`     | `<audio controls>` strip                            |
 *   | `pdf`       | document glyph + prepared-text snippet (if any)    |
 *   | `spreadsheet`| sheet glyph + prepared-table snippet (if any)      |
 *   | `document`  | document glyph + prepared-text snippet (if any)    |
 *   | `code`      | code glyph (no preview body)                        |
 *   | `file`      | generic file glyph (no preview body)                |
 *
 * A document / spreadsheet chip shows the *icon* as its visual anchor
 * (the criteria call for "a document icon") and, when the gateway has
 * already prepared a preview for the file, a short inline snippet of
 * it next to the filename. The snippet is best-effort: a chip without a
 * prepared preview simply shows the icon, never a misleading blank.
 */
import type { AttachmentKind } from "./attachment-types.ts";

/**
 * The inline presentation a composer chip uses.
 *
 * - `image`     — a still thumbnail (`<img>`).
 * - `video`     — a playable video frame (`<video>`).
 * - `audio`     — a playable audio strip (`<audio controls>`).
 * - `document`  — a document-family glyph, optionally with a text /
 *                 tabular snippet.
 * - `icon`      — a plain type glyph, no preview body (code / file).
 */
export type ComposerInlinePreviewKind = "image" | "video" | "audio" | "document" | "icon";

/** The attachment fields this module reads. */
export interface ComposerPreviewCandidate {
  kind?: string;
  mimeType?: string;
  mime?: string;
  filename?: string;
  name?: string;
  /** Gateway preview derivative (image thumbnail). */
  previewUrl?: string;
  /** Server-extracted text for a document-family file. */
  text?: string;
  /** Prepared tabular preview (columns + rows). */
  tabular?: {
    columns?: string[];
    rowCount?: number;
    sampleRows?: string[][];
    /** JAISIU-3533 — the full row set of the primary sheet. */
    rows?: string[][];
    /** JAISIU-3533 — rows above the detected header. */
    preamble?: string[][];
    /** JAISIU-3533 — every sheet of a multi-sheet workbook. */
    sheets?: Array<{
      name: string;
      index: number;
      columns: string[];
      rows: string[][];
      rowCount: number;
      preamble: string[][];
    }>;
  };
}

/**
 * How many characters of a prepared text preview the chip shows.
 *
 * Deliberately short: the chip is 260px wide at most and the *full*
 * text belongs in the overlay. This is a glance, not a reader.
 */
export const COMPOSER_PREVIEW_SNIPPET_CHARS = 120;

/**
 * Decide the inline presentation for a composer chip.
 *
 * The kind is resolved from the MIME first (the finer signal) and from
 * the gateway's coarse kind second — the same precedence the chip's
 * icon already uses (see `composerRenderKind` in
 * `chat-composer-attachments.ts`), so the inline preview and the icon
 * can never disagree about what the file is.
 */
export function composerInlinePreviewKind(
  att: ComposerPreviewCandidate,
  renderKind: AttachmentKind,
): ComposerInlinePreviewKind {
  switch (renderKind) {
    case "image":
      return "image";
    case "video":
      return "video";
    case "audio":
      return "audio";
    case "pdf":
    case "spreadsheet":
    case "document":
      return "document";
    case "code":
    case "file":
    default:
      return "icon";
  }
}

/**
 * A short, single-line snippet of a prepared document preview.
 *
 * Returns `null` when the gateway prepared nothing — the chip then
 * shows the icon alone rather than an empty box.
 */
export function composerPreviewSnippet(
  att: ComposerPreviewCandidate,
  renderKind: AttachmentKind,
): string | null {
  if (renderKind === "spreadsheet") {
    const cols = att.tabular?.columns ?? [];
    if (cols.length === 0) return null;
    // JAISIU-3533 — the chip now shows the sheet's *first data row*,
    // not just its column names. "Instrument | Ticker | Category" tells
    // the operator what the file holds; "COCOA | COCOA | CFD | SELL"
    // tells them what is in it.
    const rows = att.tabular?.rows ?? att.tabular?.sampleRows ?? [];
    const firstRow = rows[0]?.map((cell) => (cell ?? "").trim()).filter(Boolean) ?? [];
    const rowCount = att.tabular?.rowCount ?? rows.length;
    const head = cols.slice(0, 4).join(", ");
    const sample = firstRow.slice(0, 4).join(", ");
    const count = rowCount > 0 ? `${rowCount} row${rowCount === 1 ? "" : "s"}` : "no rows";
    return sample
      ? `${head} · ${count} — ${sample}`
      : `${head} · ${count}`;
  }
  if (renderKind === "document" || renderKind === "pdf") {
    const text = (att.text ?? "").trim();
    if (!text) return null;
    const flat = text.replace(/\s+/g, " ").trim();
    if (flat.length <= COMPOSER_PREVIEW_SNIPPET_CHARS) return flat;
    return `${flat.slice(0, COMPOSER_PREVIEW_SNIPPET_CHARS).trimEnd()}…`;
  }
  return null;
}

/**
 * The source URL an inline `<img>` / `<video>` / `<audio>` should use.
 *
 * Precedence:
 *   1. the gateway's preview derivative (`previewUrl`) — a small,
 *      cheap image that is exactly what the operator expects to see;
 *   2. the original `dataUrl` (a pre-upload blob) — still the local
 *      file's bytes;
 *   3. `url` — the full-resolution original.
 *
 * `fallbackUrl` is what the element falls back to when the derivative
 * 404s (the same "derivative pruned" case the transcript side handles).
 */
export function composerInlinePreviewSrc(
  att: ComposerPreviewCandidate & { dataUrl?: string; url?: string },
  fallbackUrl?: string,
): { src: string; fallbackTo?: string } {
  const src = att.previewUrl ?? att.dataUrl ?? att.url ?? "";
  const fallbackTo = fallbackUrl ?? att.url ?? att.dataUrl ?? undefined;
  if (!src) return { src: "" };
  if (!fallbackTo || fallbackTo === src) return { src };
  return { src, fallbackTo };
}
